import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useEffect, useRef, useState } from "react";

import { PADX, RAIL_BLEED_RIGHT, RAIL_PEEK } from "../constants";
import { SectionHeader } from "../SectionHeader";
import { type BrandVideo } from "../types";
import { VideoThumbnail } from "../VideoThumbnail";

/**
 * The rail never shows more than three videos at once — past that the cards get
 * too small to read a thumbnail, and a brand with a deep library is better
 * served by paging than by shrinking.
 */
const MAX_COLUMNS = 3;

/**
 * Narrowest a card may get before its 16:9 thumbnail stops reading and the
 * title starts wrapping to three lines. The column count falls out of this
 * rather than out of a breakpoint table — the rail sits inside the page
 * gutters, so its own width is what decides how many cards fit, and that is
 * not always a fixed offset from the viewport.
 */
const MIN_CARD_WIDTH = 300;
const CARD_GAP = 16;

const columnsForRailWidth = (railWidth: number): number => {
  if (railWidth <= 0) {
    return MAX_COLUMNS;
  }
  const fits = Math.floor(
    (railWidth + CARD_GAP) / (MIN_CARD_WIDTH + CARD_GAP)
  );
  return Math.min(Math.max(fits, 1), MAX_COLUMNS);
};

/**
 * How many cards fit in the rail, kept in sync as it resizes.
 *
 * The rail used to hardcode 1/2/3-up in media queries, which meant nothing else
 * could reason about it: the header could not tell whether its paging arrows
 * had anywhere to go, and a brand with fewer videos than columns left the rest
 * of the row empty. Measuring the rail lets both the card width and the
 * controls follow from one number.
 *
 * ResizeObserver, not a `resize` listener or matchMedia: the browser-pane
 * viewport emulator relays out the page WITHOUT dispatching `resize` or firing
 * media-query change events, so both of those silently kept a stale column
 * count through a live 1440 -> 800 resize (measured). ResizeObserver watches
 * the element's own layout box, so it cannot miss a relayout — and it also
 * covers the rail being narrower than the viewport implies.
 */
const useVisibleColumns = (
  railRef: React.RefObject<HTMLDivElement | null>
): number => {
  const [columns, setColumns] = useState<number>(MAX_COLUMNS);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }
    const sync = () => setColumns(columnsForRailWidth(rail.clientWidth));
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [railRef]);

  return columns;
};

// Card width is one rule driven by --video-cols rather than a stack of media
// queries: equal-specificity arbitrary widths can lose the cascade race in dev
// HMR (the later-generated `sm:` beating `lg:`), leaving a sliced card. The
// width and the rail gap read the same --grid-gutter, so N cards + (N-1) gaps
// always sum to exactly 100%.
//
// Below `md` the card gives up RAIL_PEEK so the next one shows at the right
// edge. That sliver is the whole affordance on touch — it says "there is more,
// swipe" in a way a full-width card cannot, which is what lets the paging
// arrows come off the header entirely at that width (Ryan, 2026-09-21).
const VIDEO_CARD_CSS = `
.pf-video-card {
  width: calc(
    (100% - (var(--video-cols, 3) - 1) * var(--grid-gutter, 16px))
    / var(--video-cols, 3)
  );
}
@media (max-width: 767px) {
  .pf-video-card {
    width: calc(100% - ${RAIL_PEEK});
  }
}
`;

interface VideoModuleProps {
  title?: string;
  videos?: BrandVideo[];
  onSeeAll?: () => void;
}

/**
 * Brand video carousel — one card per video, sized to fit a whole number in view
 * (1 on mobile, 2 from `sm`, 3 from `lg`) with CSS scroll-snap so it never rests
 * on a partially-sliced card; the header arrows page to the next set. "See all"
 * opens the full videos page. Play icon on hover.
 */
export const VideoModule: React.FC<VideoModuleProps> = ({
  title = "Behind the scenes",
  videos = [],
  onSeeAll,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const visibleColumns = useVisibleColumns(scrollerRef);

  const scrollByPage = (direction: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    // Page by a full viewport; scroll-snap lands it on the next whole card.
    scroller.scrollBy({
      left: direction * scroller.clientWidth,
      behavior: "smooth",
    });
  };

  if (videos.length === 0) return null;

  // A single video reads better as a feature row than a lonely carousel card:
  // thumbnail on the left, all the info (title, date, story, tags) on the right.
  if (videos.length === 1) {
    const [video] = videos;
    return (
      <div className={cn("pt-6 pb-4", PADX)}>
        <SectionHeader title={title} showArrows={false} showSeeAll={false} />
        <button
          type="button"
          className="group grid grid-cols-1 gap-6 text-left md:grid-cols-2 md:items-center"
        >
          <VideoThumbnail video={video} />
          <div className="flex flex-col gap-2">
            <span className="type-fs-subheading-medium text-fs-text-primary">
              {video.title}
            </span>
            {video.date ? (
              <span className="type-fs-label text-fs-text-subdued">
                {video.date}
              </span>
            ) : null}
            {video.description ? (
              <p className="type-fs-paragraph text-fs-text-primary max-w-xl">
                {video.description}
              </p>
            ) : null}
            {video.tags && video.tags.length > 0 ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="type-fs-label bg-fs-surface-secondary text-fs-text-subdued rounded-full px-2.5 py-1"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </button>
      </div>
    );
  }

  // A brand with fewer videos than the rail has columns gets wider cards, not a
  // half-empty row: two videos at `lg` used to sit in the first two of three
  // columns with a third of the row blank. Capped at MAX_COLUMNS so a deep
  // library still pages instead of shrinking (Ryan, 2026-09-02).
  const columns = Math.min(videos.length, visibleColumns);
  // Paging is only real when the set outruns the columns actually on screen —
  // which is a question about width, not just about count. Ora Cacao's three
  // videos fit at `lg` and need paging at `sm`, so a count threshold alone gets
  // one of those two wrong. Arrows that cannot move, and a "See all N videos"
  // link to a page holding the N already in front of you, promise more content
  // than exists.
  const needsPaging = videos.length > visibleColumns;

  return (
    <div className={cn("pt-6 pb-4", PADX)}>
      <SectionHeader
        title={title}
        seeAllLabel={`See all ${videos.length} videos`}
        onSeeAll={needsPaging ? onSeeAll : undefined}
        showSeeAll={needsPaging}
        showArrows={needsPaging}
        arrowsDesktopOnly
        onPrev={() => scrollByPage(-1)}
        onNext={() => scrollByPage(1)}
      />
      <style>{VIDEO_CARD_CSS}</style>
      <div
        ref={scrollerRef}
        style={{ "--video-cols": columns } as React.CSSProperties}
        className={cn(
          "flex snap-x snap-mandatory gap-[var(--grid-gutter,16px)] overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          RAIL_BLEED_RIGHT
        )}
      >
        {videos.map((video) => (
          <button
            key={video.title}
            type="button"
            className="pf-video-card group flex flex-shrink-0 snap-start flex-col gap-2 text-left"
          >
            <VideoThumbnail video={video} />
            <div className="flex flex-col gap-0.5">
              <span className="type-fs-paragraph-medium text-fs-text-primary">
                {video.title}
              </span>
              {video.date ? (
                <span className="type-fs-paragraph text-fs-text-subdued">
                  {video.date}
                </span>
              ) : null}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
